import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, BellRing, CheckCircle2, XCircle, MapPin, Clock, Radio } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/auth';
import { useAuth } from '../../lib/auth';

function fmtType(v) {
  return String(v || 'dispatch').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function msLeft(job) {
  if (!job?.sla_due_at) return null;
  const due = new Date(job.sla_due_at).getTime();
  if (!Number.isFinite(due)) return null;
  return Math.max(0, due - Date.now());
}

function fmtCountdown(ms) {
  if (ms == null) return 'No SLA';
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function DispatchAlertOverlay() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [flash, setFlash] = useState(false);
  const lastAlertRef = useRef(null);
  const audioCtxRef = useRef(null);
  const toneIntervalRef = useRef(null);
  const vibrateIntervalRef = useRef(null);

  const isOfficer = user?.role === 'security_officer';
  const activeJob = jobs?.[0] || null;

  const stopAlertEffects = useCallback(() => {
    if (toneIntervalRef.current) clearInterval(toneIntervalRef.current);
    if (vibrateIntervalRef.current) clearInterval(vibrateIntervalRef.current);
    toneIntervalRef.current = null;
    vibrateIntervalRef.current = null;
    try { navigator.vibrate?.(0); } catch {}
    try { audioCtxRef.current?.close?.(); } catch {}
    audioCtxRef.current = null;
  }, []);

  const playToneBurst = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      audioCtxRef.current = ctx;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = 880;
      gain.gain.value = 0.09;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();

      setTimeout(() => { osc.frequency.value = 660; }, 170);
      setTimeout(() => { osc.frequency.value = 990; }, 340);
      setTimeout(() => {
        try { osc.stop(); ctx.close(); } catch {}
      }, 650);
    } catch {}
  }, []);

  const startAlertEffects = useCallback((job) => {
    stopAlertEffects();

    try { navigator.vibrate?.([400, 200, 400, 200, 900]); } catch {}
    playToneBurst();

    toneIntervalRef.current = setInterval(playToneBurst, 3500);
    vibrateIntervalRef.current = setInterval(() => {
      try { navigator.vibrate?.([400, 200, 400, 200, 900]); } catch {}
    }, 4500);

    toast.warning('🚨 NEW DISPATCH', {
      description: `${fmtType(job.job_type)} — ${job.site_name || 'Site pending'}`,
      duration: 10000,
    });

    api.post(`/adhoc-jobs/${job.id}/viewed`).catch(() => {});
  }, [playToneBurst, stopAlertEffects]);

  const poll = useCallback(async () => {
    if (!isOfficer) return;
    try {
      const res = await api.get('/adhoc-jobs/dispatch/pending');
      const incoming = Array.isArray(res.data) ? res.data : [];
      setJobs(incoming);

      if (incoming[0]?.id && incoming[0].id !== lastAlertRef.current) {
        lastAlertRef.current = incoming[0].id;
        startAlertEffects(incoming[0]);
      }
    } catch {}
  }, [isOfficer, startAlertEffects]);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 3000);
    return () => clearInterval(t);
  }, [poll]);

  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setFlash(x => !x), 500);
    return () => clearInterval(t);
  }, []);

  useEffect(() => () => stopAlertEffects(), [stopAlertEffects]);

  const countdown = useMemo(() => fmtCountdown(msLeft(activeJob)), [activeJob, tick]);

  const acceptJob = async () => {
    if (!activeJob) return;
    setBusy(true);
    try {
      await api.post(`/adhoc-jobs/${activeJob.id}/accept`, {});
      stopAlertEffects();
      toast.success('Dispatch accepted');
      setJobs(prev => prev.filter(j => j.id !== activeJob.id));
      navigate('/adhoc-jobs');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to accept dispatch');
    } finally {
      setBusy(false);
    }
  };

  const declineJob = async () => {
    if (!activeJob) return;
    setBusy(true);
    try {
      await api.post(`/adhoc-jobs/${activeJob.id}/decline`);
      stopAlertEffects();
      toast.info('Dispatch declined');
      setJobs(prev => prev.filter(j => j.id !== activeJob.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to decline dispatch');
    } finally {
      setBusy(false);
    }
  };

  if (!isOfficer || !activeJob) return null;

  return (
    <div className={`fixed inset-0 z-[100000] flex items-center justify-center p-4 transition-colors duration-300 ${
      flash ? 'bg-red-950/95' : 'bg-black/90'
    }`}>
      <div className={`w-full max-w-md rounded-3xl border-4 overflow-hidden shadow-2xl ${
        flash ? 'border-white shadow-red-500/70' : 'border-red-500 shadow-red-500/40'
      } bg-zinc-950`}>
        <div className="bg-red-700 text-white px-5 py-4 flex items-center gap-3">
          <BellRing className="w-8 h-8 animate-bounce" />
          <div>
            <div className="text-2xl font-black tracking-wide">NEW DISPATCH</div>
            <div className="text-xs uppercase opacity-90">Immediate response required</div>
          </div>
        </div>

        <div className="p-5 space-y-4 text-white">
          <div className="flex items-start gap-3">
            <Radio className="w-9 h-9 text-red-400 shrink-0 mt-1" />
            <div>
              <div className="text-3xl font-black">{fmtType(activeJob.job_type)}</div>
              <div className="text-sm text-zinc-300">Priority: {String(activeJob.priority || 'normal').toUpperCase()}</div>
            </div>
          </div>

          <div className="rounded-2xl bg-zinc-900 border border-zinc-700 p-4 space-y-3">
            <div className="flex gap-2 text-zinc-100">
              <MapPin className="w-5 h-5 text-blue-400 shrink-0" />
              <div>
                <div className="font-bold text-lg">{activeJob.site_name || 'Unknown Site'}</div>
                <div className="text-sm text-zinc-400">{activeJob.client_name || ''}</div>
              </div>
            </div>

            <div className="flex gap-2 text-amber-300">
              <Clock className="w-5 h-5 shrink-0" />
              <div className="font-black text-xl">SLA: {countdown}</div>
            </div>
          </div>

          {activeJob.instructions && (
            <div className="rounded-2xl bg-amber-500/10 border border-amber-500/30 p-4 text-sm text-amber-100">
              {activeJob.instructions}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 pt-2">
            <button
              onClick={declineJob}
              disabled={busy}
              className="min-h-[64px] rounded-2xl bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 font-black flex items-center justify-center gap-2"
            >
              <XCircle className="w-6 h-6" /> Decline
            </button>

            <button
              onClick={acceptJob}
              disabled={busy}
              className="min-h-[64px] rounded-2xl bg-emerald-600 hover:bg-emerald-500 font-black flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/30"
            >
              <CheckCircle2 className="w-6 h-6" /> Accept
            </button>
          </div>

          <div className="text-center text-xs text-zinc-500">
            Job #{activeJob.job_number || activeJob.id}
          </div>
        </div>
      </div>
    </div>
  );
}
